import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Globe, User, LogOut, Home, Activity, Bell, Settings, HelpCircle, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, loading, logout } = useAuth();

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuOpen && !event.target.closest('[data-profile-menu]')) {
        setProfileMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [profileMenuOpen]);

  const navLinks = [
    { href: '/', label: t('nav.home') },
    { href: '/senders', label: t('nav.senders') },
    { href: '/colmados', label: t('nav.colmados') },
    { href: '/about', label: t('nav.about') },
    { href: '/trust', label: t('nav.trust') },
  ];

  const isActive = (path) => location.pathname === path;

  // Get the appropriate home path based on user role
  const getHomePath = () => {
    if (!isAuthenticated || !user) return '/';
    const roleHomeMap = {
      sender: '/app/sender',
      merchant: '/app/merchant',
      admin: '/admin/dashboard',
      beneficiary: '/beneficiary'
    };
    return roleHomeMap[user.role] || '/app/sender';
  };

  // Handle logo click - go to appropriate home
  const handleLogoClick = (e) => {
    e.preventDefault();
    navigate(getHomePath());
    setMobileMenuOpen(false);
  };

  // Handle logout
  const handleLogout = () => {
    logout();
    setProfileMenuOpen(false);
    setMobileMenuOpen(false);
    navigate('/');
  };

  // Get user display name
  const getUserDisplayName = () => {
    if (!user) return '';
    return user.name || user.first_name || user.email?.split('@')[0] || 'User';
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    const name = getUserDisplayName();
    return name.charAt(0).toUpperCase();
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200/50">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-16">
          {/* Logo - Context-aware navigation */}
          <a 
            href={getHomePath()} 
            onClick={handleLogoClick} 
            className="flex items-center gap-2" 
            data-testid="logo-link"
          >
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-600 to-emerald-700 flex items-center justify-center">
              <span className="text-white font-bold text-lg">P</span>
            </div>
            <span className="font-bold text-xl text-slate-900">PeCash</span>
          </a>

          {/* Desktop Navigation - Only show public nav links when NOT authenticated */}
          {!isAuthenticated && (
            <nav className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  data-testid={`nav-${link.href.replace('/', '') || 'home'}`}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive(link.href)
                      ? 'text-emerald-700 bg-emerald-50'
                      : 'text-slate-600 hover:text-emerald-700 hover:bg-emerald-50/50'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          )}

          {/* Right Actions */}
          <div className="flex items-center gap-3">
            {/* Language Toggle - Shows both options */}
            <div className="flex items-center gap-1 px-2 py-1 rounded-full border border-slate-200 text-sm">
              <Globe className="w-4 h-4 text-slate-500 mr-1" />
              <button
                onClick={() => language !== 'en' && toggleLanguage()}
                data-testid="lang-en"
                className={`px-2 py-0.5 rounded-full transition-colors ${
                  language === 'en' 
                    ? 'bg-emerald-100 text-emerald-700 font-medium' 
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                EN
              </button>
              <span className="text-slate-300">|</span>
              <button
                onClick={() => language !== 'es' && toggleLanguage()}
                data-testid="lang-es"
                className={`px-2 py-0.5 rounded-full transition-colors ${
                  language === 'es' 
                    ? 'bg-emerald-100 text-emerald-700 font-medium' 
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                ES
              </button>
            </div>

            {/* Loading state */}
            {loading && (
              <div className="w-8 h-8 rounded-full bg-slate-100 animate-pulse" />
            )}

            {/* Authenticated User Menu */}
            {!loading && isAuthenticated && user && (
              <div className="relative" data-profile-menu>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setProfileMenuOpen(!profileMenuOpen);
                  }}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-200 hover:bg-slate-50 transition-colors"
                  data-testid="user-menu-btn"
                >
                  <div className="w-7 h-7 rounded-full bg-emerald-600 flex items-center justify-center">
                    <span className="text-white text-sm font-medium">{getUserInitials()}</span>
                  </div>
                  <span className="hidden sm:inline text-sm font-medium text-slate-700 max-w-[100px] truncate">
                    {getUserDisplayName()}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${profileMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Profile Dropdown */}
                <AnimatePresence>
                  {profileMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 8, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 8, scale: 0.96 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50"
                      data-testid="user-dropdown"
                    >
                      {/* User Info */}
                      <div className="px-4 py-2 border-b border-slate-100">
                        <p className="text-sm font-medium text-slate-900 truncate">{getUserDisplayName()}</p>
                        <p className="text-xs text-slate-500 truncate">{user.email}</p>
                        <span className="inline-block mt-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full capitalize">
                          {user.role}
                        </span>
                      </div>

                      {/* Menu Items */}
                      <div className="py-1">
                        <button
                          onClick={() => { navigate(getHomePath()); setProfileMenuOpen(false); }}
                          className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                          data-testid="menu-home"
                        >
                          <Home className="w-4 h-4" />
                          Home
                        </button>
                        <button
                          onClick={() => { navigate(`${getHomePath()}?tab=profile`); setProfileMenuOpen(false); }}
                          className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                          data-testid="menu-profile"
                        >
                          <User className="w-4 h-4" />
                          Profile
                        </button>
                        <button
                          onClick={() => { navigate(`${getHomePath()}?tab=activity`); setProfileMenuOpen(false); }}
                          className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                          data-testid="menu-activity"
                        >
                          <Activity className="w-4 h-4" />
                          Activity
                        </button>
                        <button
                          onClick={() => { navigate(`${getHomePath()}?tab=notifications`); setProfileMenuOpen(false); }}
                          className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                          data-testid="menu-notifications"
                        >
                          <Bell className="w-4 h-4" />
                          Notifications
                        </button>
                        <button
                          onClick={() => { navigate(`${getHomePath()}?tab=security`); setProfileMenuOpen(false); }}
                          className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                          data-testid="menu-settings"
                        >
                          <Settings className="w-4 h-4" />
                          Settings
                        </button>
                        <button
                          onClick={() => { setProfileMenuOpen(false); }}
                          className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                          data-testid="menu-help"
                        >
                          <HelpCircle className="w-4 h-4" />
                          Help
                        </button>
                      </div>

                      {/* Logout */}
                      <div className="border-t border-slate-100 pt-1">
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                          data-testid="menu-logout"
                        >
                          <LogOut className="w-4 h-4" />
                          Log Out
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* Unauthenticated - Login/Get Started */}
            {!loading && !isAuthenticated && (
              <div className="hidden md:flex items-center gap-2">
                <Link to="/login">
                  <Button variant="ghost" data-testid="login-btn" className="text-slate-600">
                    {t('nav.login')}
                  </Button>
                </Link>
                <Link to="/login">
                  <Button
                    data-testid="get-started-btn"
                    className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full px-6"
                  >
                    {t('nav.getStarted')}
                  </Button>
                </Link>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
              className="md:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-b border-slate-200 overflow-hidden"
          >
            <nav className="px-6 py-4 space-y-1">
              {/* Show user info if authenticated */}
              {isAuthenticated && user && (
                <div className="pb-4 mb-4 border-b border-slate-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center">
                      <span className="text-white font-medium">{getUserInitials()}</span>
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{getUserDisplayName()}</p>
                      <p className="text-sm text-slate-500">{user.email}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Public nav links - only when NOT authenticated */}
              {!isAuthenticated && navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${link.href.replace('/', '') || 'home'}`}
                  className={`block px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                    isActive(link.href)
                      ? 'text-emerald-700 bg-emerald-50'
                      : 'text-slate-600 hover:text-emerald-700 hover:bg-emerald-50/50'
                  }`}
                >
                  {link.label}
                </Link>
              ))}

              {/* Authenticated mobile menu items */}
              {isAuthenticated && user && (
                <>
                  <button
                    onClick={() => { navigate(getHomePath()); setMobileMenuOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium text-slate-600 hover:text-emerald-700 hover:bg-emerald-50/50"
                  >
                    <Home className="w-5 h-5" />
                    Home
                  </button>
                  <button
                    onClick={() => { navigate(getHomePath()); setMobileMenuOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium text-slate-600 hover:text-emerald-700 hover:bg-emerald-50/50"
                  >
                    <User className="w-5 h-5" />
                    Profile
                  </button>
                  <button
                    onClick={() => { navigate(getHomePath()); setMobileMenuOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium text-slate-600 hover:text-emerald-700 hover:bg-emerald-50/50"
                  >
                    <Activity className="w-5 h-5" />
                    Activity
                  </button>
                  <button
                    onClick={() => { navigate(getHomePath()); setMobileMenuOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium text-slate-600 hover:text-emerald-700 hover:bg-emerald-50/50"
                  >
                    <Bell className="w-5 h-5" />
                    Notifications
                  </button>
                </>
              )}

              <div className="pt-4 space-y-2">
                {/* Unauthenticated - show login buttons */}
                {!isAuthenticated && (
                  <>
                    <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                      <Button
                        variant="outline"
                        data-testid="mobile-login-btn"
                        className="w-full justify-center"
                      >
                        {t('nav.login')}
                      </Button>
                    </Link>
                    <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                      <Button
                        data-testid="mobile-get-started-btn"
                        className="w-full justify-center bg-emerald-700 hover:bg-emerald-800 text-white"
                      >
                        {t('nav.getStarted')}
                      </Button>
                    </Link>
                  </>
                )}

                {/* Authenticated - show logout */}
                {isAuthenticated && (
                  <Button
                    onClick={handleLogout}
                    variant="outline"
                    className="w-full justify-center text-red-600 border-red-200 hover:bg-red-50"
                    data-testid="mobile-logout-btn"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Log Out
                  </Button>
                )}
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
